import { useCallback, useEffect, useRef, useState } from 'react'
import { isExtensionInstalled } from '@/lib/extensionUtils'

const RETRY_INTERVAL_MS = 5000

/**
 * Shared "is Screenata Capture installed?" state.
 *
 * Detection is a postMessage handshake answered by the extension's content
 * script, which only boots at `document_idle` after an async init. A single
 * probe on mount therefore races that boot, and a lost race used to latch a
 * false negative for the rest of the session — which is why "Capture in
 * browser" kept offering the Chrome Web Store to users who already had the
 * extension. So keep probing until the extension answers, and re-probe when
 * the tab regains focus (the user may have just installed it).
 *
 * Returns `null` until the first probe settles, then a boolean. The setter
 * lets callers record what a real capture handshake proved.
 */
export function useExtensionInstalled(): [
  boolean | null,
  (installed: boolean) => void,
] {
  const [installed, setInstalled] = useState<boolean | null>(null)
  const detectedRef = useRef(false)

  const markInstalled = useCallback((value: boolean) => {
    if (value) detectedRef.current = true
    setInstalled(value)
  }, [])

  useEffect(() => {
    let cancelled = false

    const probe = () => {
      if (detectedRef.current) return
      void isExtensionInstalled().then((found) => {
        if (cancelled || detectedRef.current) return
        setInstalled(found)
        if (found) {
          detectedRef.current = true
          clearInterval(interval)
        }
      })
    }

    const interval = setInterval(probe, RETRY_INTERVAL_MS)
    probe()
    window.addEventListener('focus', probe)

    return () => {
      cancelled = true
      clearInterval(interval)
      window.removeEventListener('focus', probe)
    }
  }, [])

  return [installed, markInstalled]
}
